import { type ReactElement } from 'react'
import { useRequiredParams } from '@util'
import { isEmpty, type CompanyData, type WithCompanyId } from '@terros/common'
import { createFileRoute } from '@tanstack/react-router'
import { useForm } from '@mantine/form'
import { MultiSelect, NumberInput, Select, TextInput } from '@mantine/core'
import { useToastError } from '@hooks'
import {
  accessStatuses,
  COMPANY_INDUSTRY_OPTIONS,
  companyKinds,
  useCompany,
  useProfile,
  useTranslation,
  useUpdateCompany,
} from '@front/model'
import {
  ButtonSave,
  CopyText,
  EditEntitlements,
  NotReady,
  Page,
  PermissionCheckWrapper,
  PreferenceFormProvider,
  UserInlineSelect,
} from '@components'
import { fillInitialValues } from '../companies_.$companyId/util'
import { SensitivePreferences } from './SensitivePreferences'

function ManageCompany(): ReactElement {
  const { companyId } = useRequiredParams<WithCompanyId>()
  const { data, isLoading, error } = useCompany({ companyId })
  useToastError(error)

  if (isLoading || error || !data) return <NotReady isLoading={isLoading} data={data} error={error} />

  return (
    <PermissionCheckWrapper targetId={companyId} permissionType='company:list' navigationTo='parent'>
      <ManageCompanyWithData data={data} />
    </PermissionCheckWrapper>
  )
}

const ManageCompanyWithData = ({ data }: { data: CompanyData }): ReactElement => {
  const { t } = useTranslation()
  const { companyId } = data
  const { isTantalim } = useProfile()

  const form = useForm<CompanyData>({
    initialValues: fillInitialValues(data),
  })
  const { resetDirty, values, getInputProps, isDirty, onSubmit, setFieldValue } = form

  const { update, isUpdating, error: updateError } = useUpdateCompany()
  useToastError(updateError)

  const submit = async (formValues: CompanyData): Promise<void> => {
    await update({
      ...formValues,
      salesforceAccountId: isEmpty(formValues.salesforceAccountId) ? null : formValues.salesforceAccountId,
    })
    resetDirty(formValues)
  }

  return (
    <form onSubmit={onSubmit(submit)}>
      <Page>
        <Page.Heading back title={data.name}>
          <ButtonSave disabled={!isDirty()} loading={isUpdating} type='submit' />
        </Page.Heading>
        <Page.Content scrollArea>
          <CopyText label={t('Company ID')} value={companyId} />
          <CopyText label={t('Salesforce Account ID')} readOnly={false} {...getInputProps('salesforceAccountId')} />
          <TextInput label={t('Name')} {...getInputProps('name')} />

          <Select
            data={companyKinds}
            label={t('Kind')}
            allowDeselect={false}
            clearable={false}
            {...getInputProps('kind')}
          />
          <Select
            data={accessStatuses}
            label={t('Access Status')}
            allowDeselect={false}
            clearable={false}
            {...getInputProps('accessStatus')}
          />
          {isTantalim && (
            <MultiSelect
              clearable
              data={COMPANY_INDUSTRY_OPTIONS}
              label={t('Industries')}
              searchable
              {...getInputProps('industries')}
            />
          )}
          <NumberInput label={t('Max Users')} {...getInputProps('maxUsers')} />
          <UserInlineSelect
            value={values.internalOwnerId}
            allowDeselect
            onChange={(userId) => setFieldValue('internalOwnerId', userId)}
            placeholder={t('Owner')}
          />
          {isTantalim && (
            <PreferenceFormProvider allowUpdate defaultGroup='all'>
              <SensitivePreferences companyId={companyId} />
            </PreferenceFormProvider>
          )}
          <EditEntitlements entityId={companyId} />
        </Page.Content>
      </Page>
    </form>
  )
}

export const Route = createFileRoute('/companies_/$companyId_/manage/')({
  component: ManageCompany,
})
